import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { sendContactMessage } from '../../services/contactService';
import { CoachMark } from '../CoachMark';
import './FeedbackFlow.css';

type FeedbackType = 'bug' | 'suggestion' | 'question' | 'other';

const FeedbackFlow: React.FC = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('suggestion');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [image, setImage] = useState<{ content: string; name: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Draggability State
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null); // Screen coordinates
  const [isDragging, setIsDragging] = useState(false);
  const dragStartPos = useRef<{ x: number; y: number; startX: number; startY: number }>({
    x: 0,
    y: 0,
    startX: 0,
    startY: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const [cardPosition, setCardPosition] = useState<{
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
    transformOrigin?: string;
  }>({ bottom: '5rem', right: '0', transformOrigin: 'bottom right' });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert(t('feedback.fileTooLarge'));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImage({
          content: reader.result as string,
          name: file.name,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => setImage(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message) return;

    setIsSubmitting(true);

    // Create detailed message for the dev team
    const detailedMessage = `[Feedback Type: ${type}]\n[Source URL: ${window.location.href}]\n\n${message}`;

    // Use the contact service to send the feedback
    const success = await sendContactMessage(
      'Feedback Flow User', // Default name for feedback
      email || 'anonymous-feedback@theychat.ai', // Default email if not provided
      detailedMessage,
      image ? [image] : undefined,
    );

    setIsSubmitting(false);

    if (success) {
      setIsSubmitted(true);
      setTimeout(() => {
        if (!isOpen) {
          resetForm();
        }
      }, 3000);
    } else {
      // Fallback or error handling could go here
      alert(t('common.error'));
    }
  };

  const resetForm = useCallback(() => {
    setIsSubmitted(false);
    setMessage('');
    setEmail('');
    setType('suggestion');
    setImage(null);
  }, []);

  const toggleOpen = useCallback(() => {
    if (isDragging) return; // Don't toggle if we just finished dragging

    if (!isOpen) {
      // Logic to snap to bottom-right when opening for best visibility
      const cornerX = window.innerWidth - 80;
      const cornerY = window.innerHeight - 80;

      setPosition({ x: cornerX, y: cornerY });

      // Standard bottom-right alignment for the card
      setCardPosition({
        bottom: '5rem',
        right: '0',
        left: 'auto',
        top: 'auto',
        transformOrigin: 'bottom right',
      });
    }

    if (isOpen && isSubmitted) {
      resetForm();
    }
    setIsOpen(!isOpen);
  }, [isOpen, isSubmitted, isDragging, resetForm]);

  // Handle Dragging
  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    // Prevent default scrolling on touch
    if (e.cancelable) e.preventDefault();

    const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;

    const container = containerRef.current;
    if (container) {
      const rect = container.getBoundingClientRect();
      dragStartPos.current = {
        x: clientX - rect.left,
        y: clientY - rect.top,
        startX: clientX,
        startY: clientY,
      };
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      // Prevent scrolling
      if (e.cancelable) e.preventDefault();

      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

      // Ensure button stays within viewport
      const newX = Math.min(Math.max(clientX - dragStartPos.current.x, 0), window.innerWidth - 60);
      const newY = Math.min(Math.max(clientY - dragStartPos.current.y, 0), window.innerHeight - 60);

      setPosition({ x: newX, y: newY });
      window.dispatchEvent(new Event('resize'));
    };

    const onEnd = (e: MouseEvent | TouchEvent) => {
      if (!isDragging) return;

      const clientX =
        'changedTouches' in e ? e.changedTouches[0].clientX : (e as MouseEvent).clientX;
      const clientY =
        'changedTouches' in e ? e.changedTouches[0].clientY : (e as MouseEvent).clientY;

      // Calculate distance moved
      const dist = Math.sqrt(
        (clientX - dragStartPos.current.startX) ** 2 + (clientY - dragStartPos.current.startY) ** 2,
      );

      // If moved less than 5px, it's a click, not a drag
      if (dist < 5) {
        setIsDragging(false);
        toggleOpen();
      } else {
        setTimeout(() => setIsDragging(false), 50);
      }
    };

    if (isDragging) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onEnd);
      window.addEventListener('touchmove', onMove, { passive: false });
      window.addEventListener('touchend', onEnd);
    }

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onEnd);
    };
  }, [isDragging, toggleOpen]);

  const containerStyle: React.CSSProperties = position
    ? {
        position: 'fixed',
        left: position.x,
        top: position.y,
        bottom: 'auto',
        right: 'auto',
        zIndex: 9999,
        transition: isDragging ? 'none' : 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      }
    : {
        transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
      };

  return (
    <div ref={containerRef} className="feedback-container" style={containerStyle}>
      {isOpen && (
        <div
          className="feedback-card"
          style={{
            bottom: cardPosition.bottom,
            top: cardPosition.top,
            left: cardPosition.left,
            right: cardPosition.right,
            transformOrigin: cardPosition.transformOrigin,
          }}
        >
          {isSubmitted ? (
            <div className="feedback-success">
              <div className="success-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <title>Success Icon</title>
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h3>{t('feedback.successTitle')}</h3>
              <p>{t('feedback.successMessage')}</p>
              <button type="button" className="btn-close-success" onClick={toggleOpen}>
                {t('feedback.close')}
              </button>
            </div>
          ) : (
            <>
              <div className="feedback-header">
                <h3>{t('feedback.title')}</h3>
                <p>{t('feedback.description')}</p>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="feedback-body">
                  <div className="feedback-field">
                    <span className="feedback-label">{t('feedback.typeLabel')}</span>
                    <div className="feedback-types">
                      {(['suggestion', 'bug', 'question', 'other'] as FeedbackType[]).map(
                        (tKey) => (
                          <button
                            key={tKey}
                            type="button"
                            className={`feedback-type-btn ${type === tKey ? 'selected' : ''}`}
                            onClick={() => setType(tKey)}
                          >
                            {t(`feedback.types.${tKey}`)}
                          </button>
                        ),
                      )}
                    </div>
                  </div>

                  <div className="feedback-field">
                    <label htmlFor="feedback-message" className="feedback-label">
                      {t('feedback.messageLabel')}
                    </label>
                    <textarea
                      id="feedback-message"
                      className="feedback-textarea"
                      placeholder={t('feedback.messagePlaceholder')}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                    />
                  </div>

                  <div className="feedback-field">
                    <label htmlFor="feedback-image-upload" className="feedback-label">
                      {t('feedback.imageLabel')}
                    </label>
                    <div className="image-upload-container">
                      {!image ? (
                        <label className="image-upload-btn">
                          <input
                            id="feedback-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            hidden
                          />
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <title>Upload Image Icon</title>
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <span>{t('feedback.uploadImage')}</span>
                        </label>
                      ) : (
                        <div className="image-preview-wrapper">
                          <img src={image.content} alt="Preview" className="image-preview" />
                          <button type="button" className="remove-image-btn" onClick={removeImage}>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <title>Remove Image Icon</title>
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="feedback-field">
                    <label htmlFor="feedback-email" className="feedback-label">
                      {t('feedback.emailLabel')}
                    </label>
                    <input
                      id="feedback-email"
                      type="email"
                      className="feedback-input"
                      placeholder={t('feedback.emailPlaceholder')}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>
                <div className="feedback-footer">
                  <button type="submit" className="btn-submit" disabled={isSubmitting || !message}>
                    {isSubmitting ? t('feedback.submitting') : t('feedback.submit')}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      <CoachMark id="feedback_button" message={t('feedback.coachMark')} position="left">
        <button
          type="button"
          className={`feedback-trigger ${isOpen ? 'active' : ''}`}
          onClick={toggleOpen}
          onMouseDown={startDrag}
          onTouchStart={startDrag}
          aria-label={t('feedback.trigger')}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          {isOpen ? (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Close Feedback Form</title>
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <title>Open Feedback Form</title>
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
            </svg>
          )}
        </button>
      </CoachMark>
    </div>
  );
};

export default FeedbackFlow;
